/**
 * Advertiser creative upload — data layer (OPUS DASH-146).
 *
 * Typed wrappers over `apiClient` for the API router mounted at
 * `/api/v1/advertiser-creative-upload`. Exports pure validation helpers
 * so the page test can assert the form contract without a DOM.
 *
 *   POST   /advertiser-creative-upload              createAsset
 *   GET    /advertiser-creative-upload/list/:id     fetchAssets
 *   POST   /advertiser-creative-upload/approve/:id  setAssetApproval
 *   DELETE /advertiser-creative-upload/:id          deleteAsset
 *
 * No React / no TanStack imports here — the page drives fetches through a
 * tiny `useState` hydration pattern. A full React-Query migration can
 * replace these helpers later without touching the API contract.
 */

import { apiClient, ApiError } from "@/api-client";

// ---------------------------------------------------------------------------
// Shared constants — mirror the API validators so the form rejects bad input
// without a round-trip.
// ---------------------------------------------------------------------------

export const CREATIVE_KINDS = ["banner", "video", "html"] as const;
export type CreativeKind = (typeof CREATIVE_KINDS)[number];

export const MAX_NAME_LEN = 255;
export const MAX_URL_LEN = 2048;
export const MAX_DIMENSION = 20_000;
export const MAX_SIZE_BYTES = 50 * 1024 * 1024 * 1024;

// ---------------------------------------------------------------------------
// Wire types
// ---------------------------------------------------------------------------

export interface CreativeAsset {
	id: string;
	advertiser_id: string;
	uploaded_by: string;
	kind: CreativeKind;
	name: string;
	url: string;
	size_bytes: number | null;
	width: number | null;
	height: number | null;
	approved: boolean;
	created_at: number;
}

export interface CreateAssetInput {
	advertiser_id: string;
	uploaded_by: string;
	kind: CreativeKind;
	name: string;
	url: string;
	size_bytes?: number | null;
	width?: number | null;
	height?: number | null;
}

export interface AssetListResponse {
	items: CreativeAsset[];
	total: number;
}

// ---------------------------------------------------------------------------
// Validation — form-level `safeParse` so the page can surface errors without
// waiting for the API round-trip.
// ---------------------------------------------------------------------------

export interface UploadFormInput {
	advertiserId?: unknown;
	uploadedBy?: unknown;
	kind?: unknown;
	name?: unknown;
	url?: unknown;
	widthRaw?: unknown;
	heightRaw?: unknown;
	sizeBytesRaw?: unknown;
}

export interface UploadFormData {
	advertiser_id: string;
	uploaded_by: string;
	kind: CreativeKind;
	name: string;
	url: string;
	width: number | null;
	height: number | null;
	size_bytes: number | null;
}

export interface FormParseOk {
	success: true;
	data: UploadFormData;
}
export interface FormParseErr {
	success: false;
	errors: Record<string, string>;
}
export type FormParseResult = FormParseOk | FormParseErr;

function asTrimmedString(v: unknown): string {
	return typeof v === "string" ? v.trim() : "";
}

function parseOptionalInt(
	raw: unknown,
	field: string,
	max: number,
	errors: Record<string, string>,
): number | null {
	if (raw === undefined || raw === null || raw === "") return null;
	const n = typeof raw === "number" ? raw : Number(raw);
	if (!Number.isFinite(n)) {
		errors[field] = `${field} must be a whole number.`;
		return null;
	}
	if (!Number.isInteger(n)) {
		errors[field] = `${field} must be a whole number.`;
		return null;
	}
	if (n < 0) {
		errors[field] = `${field} cannot be negative.`;
		return null;
	}
	if (n > max) {
		errors[field] = `${field} exceeds the allowed maximum.`;
		return null;
	}
	return n;
}

export function isValidHttpUrl(v: unknown): v is string {
	if (typeof v !== "string") return false;
	const trimmed = v.trim();
	if (trimmed.length === 0 || trimmed.length > MAX_URL_LEN) return false;
	if (!/^https?:\/\//i.test(trimmed)) return false;
	try {
		// URL constructor throws on malformed input.
		new URL(trimmed);
	} catch {
		return false;
	}
	return true;
}

export const uploadFormSchema = {
	safeParse(input: UploadFormInput): FormParseResult {
		const errors: Record<string, string> = {};

		const advertiserId = asTrimmedString(input.advertiserId);
		if (advertiserId.length === 0) {
			errors.advertiserId = "Advertiser ID is required.";
		}

		const uploadedBy = asTrimmedString(input.uploadedBy);
		if (uploadedBy.length === 0) {
			errors.uploadedBy = "Uploader is required.";
		}

		const kindRaw = asTrimmedString(input.kind).toLowerCase();
		if (kindRaw.length === 0) {
			errors.kind = "Kind is required.";
		} else if (!CREATIVE_KINDS.includes(kindRaw as CreativeKind)) {
			errors.kind = "Kind must be banner, video, or html.";
		}

		const name = asTrimmedString(input.name);
		if (name.length === 0) {
			errors.name = "Name is required.";
		} else if (name.length > MAX_NAME_LEN) {
			errors.name = `Name must be ${MAX_NAME_LEN} characters or fewer.`;
		}

		const url = asTrimmedString(input.url);
		if (url.length === 0) {
			errors.url = "URL is required.";
		} else if (!isValidHttpUrl(url)) {
			errors.url = "URL must be a valid http(s) link.";
		}

		const width = parseOptionalInt(
			input.widthRaw,
			"width",
			MAX_DIMENSION,
			errors,
		);
		const height = parseOptionalInt(
			input.heightRaw,
			"height",
			MAX_DIMENSION,
			errors,
		);
		const sizeBytes = parseOptionalInt(
			input.sizeBytesRaw,
			"sizeBytes",
			MAX_SIZE_BYTES,
			errors,
		);

		if (Object.keys(errors).length > 0) {
			return { success: false, errors };
		}

		return {
			success: true,
			data: {
				advertiser_id: advertiserId,
				uploaded_by: uploadedBy,
				kind: kindRaw as CreativeKind,
				name,
				url,
				width,
				height,
				size_bytes: sizeBytes,
			},
		};
	},
};

// ---------------------------------------------------------------------------
// API wrappers
// ---------------------------------------------------------------------------

const ROOT = "/advertiser-creative-upload";

export async function createAsset(
	input: CreateAssetInput,
): Promise<CreativeAsset> {
	const body = {
		advertiser_id: input.advertiser_id,
		uploaded_by: input.uploaded_by,
		kind: input.kind,
		name: input.name,
		url: input.url,
		...(input.size_bytes !== undefined
			? { size_bytes: input.size_bytes }
			: {}),
		...(input.width !== undefined ? { width: input.width } : {}),
		...(input.height !== undefined ? { height: input.height } : {}),
	};
	const res = await apiClient.post<{ asset: CreativeAsset }>(ROOT, body);
	return res.asset;
}

export async function fetchAssets(
	advertiserId: string,
	limit?: number,
): Promise<CreativeAsset[]> {
	if (!advertiserId || advertiserId.trim().length === 0) return [];
	try {
		const res = await apiClient.get<AssetListResponse | CreativeAsset[]>(
			`${ROOT}/list/${encodeURIComponent(advertiserId)}`,
			limit ? { query: { limit } } : undefined,
		);
		return Array.isArray(res) ? res : (res.items ?? []);
	} catch (err) {
		if (
			err instanceof ApiError &&
			(err.status === 401 || err.status === 403 || err.status === 404)
		) {
			return [];
		}
		throw err;
	}
}

export async function setAssetApproval(
	id: string,
	approved: boolean,
): Promise<CreativeAsset> {
	const res = await apiClient.post<{ asset: CreativeAsset }>(
		`${ROOT}/approve/${encodeURIComponent(id)}`,
		{ approved },
	);
	return res.asset;
}

export async function deleteAsset(
	id: string,
): Promise<{ deleted: boolean; id: string }> {
	return apiClient.delete<{ deleted: boolean; id: string }>(
		`${ROOT}/${encodeURIComponent(id)}`,
	);
}

// ---------------------------------------------------------------------------
// Presigned-URL stub — for the upload form UX.
//
// The real presigned-upload endpoint lives elsewhere; the landing form accepts
// a URL the user has already generated (or pasted). This helper derives a
// stable placeholder so the form can demo the flow even when the backend
// stub isn't wired up yet.
// ---------------------------------------------------------------------------

export function buildPlaceholderUrl(
	advertiserId: string,
	filename: string,
): string {
	const host = "https://uploads.trackerity.example";
	const encodedAdv = encodeURIComponent(advertiserId || "adv");
	const encodedFile = encodeURIComponent(filename || "asset.bin");
	return `${host}/${encodedAdv}/${encodedFile}`;
}

// ---------------------------------------------------------------------------
// Display helpers — pure, tested.
// ---------------------------------------------------------------------------

export function approvalBadgeLabel(approved: boolean): string {
	return approved ? "Approved" : "Pending";
}

export function formatBytes(bytes: number | null): string {
	if (bytes === null || bytes === undefined) return "—";
	if (bytes < 1024) return `${bytes} B`;
	const kb = bytes / 1024;
	if (kb < 1024) return `${kb.toFixed(1)} KB`;
	const mb = kb / 1024;
	if (mb < 1024) return `${mb.toFixed(1)} MB`;
	const gb = mb / 1024;
	return `${gb.toFixed(2)} GB`;
}

export function formatDimensions(
	width: number | null,
	height: number | null,
): string {
	if (width === null && height === null) return "—";
	const w = width ?? "?";
	const h = height ?? "?";
	return `${w}×${h}`;
}

export const advertiserCreativeUploadPaths = {
	root: ROOT,
	list: (advertiserId: string) =>
		`${ROOT}/list/${encodeURIComponent(advertiserId)}`,
	approve: (id: string) => `${ROOT}/approve/${encodeURIComponent(id)}`,
	delete: (id: string) => `${ROOT}/${encodeURIComponent(id)}`,
} as const;
